import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/types";
import { formatDate } from "@/utils/catalog";

interface DramaCardProps {
  drama: Drama;
  variant?: "grid" | "list" | "compact";
  showSummary?: boolean;
}

function MetaLine({ drama }: { drama: Drama }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
      <span className="font-label">{drama.year}</span>
      <span className="h-1 w-1 rounded-full bg-background-400" />
      <span>{drama.episodes} 集</span>
      <span className="h-1 w-1 rounded-full bg-background-400" />
      <span>{drama.region}</span>
    </div>
  );
}

export default function DramaCard({ drama, variant = "grid", showSummary = false }: DramaCardProps) {
  if (variant === "list") {
    return (
      <Link
        to={`/detail/${drama.id}`}
        className="group flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-3 transition-colors duration-300 hover:border-accent-300 md:gap-6 md:p-4"
      >
        <div className="relative h-[132px] w-[96px] shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[168px] md:w-[120px]">
          <img
            src={drama.cover}
            alt={`${drama.title} 剧集封面`}
            title={`${drama.title} 日本伦理剧`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {drama.types.slice(0, 2).map((type) => (
                <span
                  key={type}
                  className="rounded-full border border-background-200 bg-background-100 px-2 py-0.5 text-[11px] text-foreground-600"
                >
                  {type}
                </span>
              ))}
              <span className="font-label text-xs font-semibold text-accent-700">{drama.rating.toFixed(1)}</span>
            </div>
            <h3 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
              {drama.title}
            </h3>
            <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-foreground-600 md:text-sm">
              {drama.tagline}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <MetaLine drama={drama} />
            <span className="hidden text-[11px] text-foreground-400 md:block">{formatDate(drama.updatedAt)} 更新</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link to={`/detail/${drama.id}`} className="group flex items-center gap-3 py-3">
        <div className="h-14 w-11 shrink-0 overflow-hidden rounded bg-background-200">
          <img
            src={drama.cover}
            alt={`${drama.title} 封面`}
            title={`${drama.title} 日本伦理剧`}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">{drama.title}</p>
          <p className="mt-0.5 text-[11px] text-foreground-500">
            {drama.year} · {drama.types[0]} · {drama.rating.toFixed(1)} 分
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/detail/${drama.id}`} className="group block">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={drama.cover}
          alt={`${drama.title} 剧集封面`}
          title={`${drama.title} ${drama.types[0]}`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-primary-950/70 to-transparent" />
        <div className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-background-50/95 px-2 py-1 font-label text-[11px] font-semibold text-foreground-900">
          <i className="ri-star-fill text-[10px] text-accent-500" />
          {drama.rating.toFixed(1)}
        </div>
        <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
          {drama.types.slice(0, 2).map((type) => (
            <span
              key={type}
              className="rounded-full bg-background-50/90 px-2 py-0.5 text-[10px] font-medium text-foreground-800"
            >
              {type}
            </span>
          ))}
        </div>
      </div>
      <div className="pt-3">
        <h3 className="line-clamp-1 font-heading text-[15px] font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
          {drama.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-foreground-600">
          {showSummary ? drama.summary : drama.tagline}
        </p>
        <div className="mt-2">
          <MetaLine drama={drama} />
        </div>
      </div>
    </Link>
  );
}

export function DramaCardSkeleton() {
  return (
    <div>
      <div className="skeleton aspect-[3/4] w-full rounded-lg" />
      <div className="skeleton mt-3 h-4 w-3/4 rounded" />
      <div className="skeleton mt-2 h-3 w-full rounded" />
      <div className="skeleton mt-2 h-3 w-1/2 rounded" />
    </div>
  );
}